import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { GradientCard } from './ui/GradientCard';
import { MapPin, Clock, AlertTriangle, Building2, ChevronRight, Droplets, Gauge, Trash2 } from 'lucide-react';

interface Well {
  id: string;
  name: string;
  location?: string;
  company?: string;
  lastEntry?: string;
  status?: string;
  wellStatus?: 'draft' | 'active'; // Draft vs finalized well
  staticPressure?: number;
  oilProduction?: number;
  waterProduction?: number;
}

interface WellsListProps {
  wells: Well[];
  selectedWell: Well | null;
  onSelectWell: (well: Well) => void;
  onEditWell?: (well: Well) => void; // For editing draft wells
  onDeleteWell?: (well: Well) => void; // For deleting any well
  overdueWells: Well[];
}

export default function WellsList({ wells, selectedWell, onSelectWell, onEditWell, onDeleteWell, overdueWells }: WellsListProps) {
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const isOverdue = (well: Well) => {
    return overdueWells.some(overdueWell => overdueWell.id === well.id);
  };

  if (wells.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p>No wells found</p>
        <p className="text-sm">Add your first well to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {wells.map((well) => {
        const isWellOverdue = isOverdue(well);
        const isDraft = well.wellStatus === 'draft';
        const isSelected = selectedWell?.id === well.id;

        let variant: 'primary' | 'action' | 'warning' | 'success' | 'entity' | 'inactive' | 'critical' = 'primary';
        if (isDraft) variant = 'inactive';
        else if (isWellOverdue) variant = 'critical';
        else if (isSelected) variant = 'action';
        
        return (
          <GradientCard
            key={well.id}
            variant={variant}
            onClick={() => onSelectWell(well)}
            className="w-full text-left"
            innerClassName={`transition-colors ${isSelected ? 'bg-rose-50/50 dark:bg-rose-900/10' : ''}`}
          >
            {/* Delete Button - Hover effect on desktop, always visible on mobile */}
            {onDeleteWell && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteWell(well);
                }}
                className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <div className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors ${
                  isDraft ? 'hover:bg-gray-200' : 'hover:bg-red-50 dark:hover:bg-red-900/20'
                }`}>
                  <Trash2 className={`h-4 w-4 ${
                    isDraft ? 'text-gray-600' : 'text-red-600'
                  }`} />
                </div>
              </div>
            )}
            <div>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className={`font-semibold truncate ${isDraft ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                      {well.name}
                    </h3>
                    {isDraft && (
                      <Badge variant="outline" className="text-xs">
                        Draft
                      </Badge>
                    )}
                    {isWellOverdue && !isDraft && (
                      <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    )}
                  </div>
                  
                  {well.location && (
                    <div className="flex items-center space-x-1.5 text-gray-600 dark:text-gray-400">
                      <MapPin className="h-4 w-4" />
                      <p className="text-sm truncate">{well.location}</p>
                    </div>
                  )}

                  {well.company && (
                    <div className="flex items-center space-x-1.5 text-gray-600 dark:text-gray-400 mt-1">
                      <Building2 className="h-4 w-4" />
                      <p className="text-sm truncate">{well.company}</p>
                    </div>
                  )}
                </div>
                <ChevronRight className="h-6 w-6 text-gray-400 flex-shrink-0 ml-2" />
              </div>

              {/* Production Info */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                {well.oilProduction !== undefined && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                    <div className="flex items-center space-x-1.5">
                      <Droplets className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <span className="text-xs text-green-700 dark:text-green-300">Oil</span>
                    </div>
                    <p className="font-semibold text-green-900 dark:text-green-100 mt-1">
                      {(well.oilProduction || 0).toFixed(1)} BPD
                    </p>
                  </div>
                )}
                
                {well.staticPressure !== undefined && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
                    <div className="flex items-center space-x-1.5">
                      <Gauge className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-xs text-blue-700 dark:text-blue-300">Gas Vol</span>
                    </div>
                    <p className="font-semibold text-blue-900 dark:text-blue-100 mt-1">
                      {(well.staticPressure || 0).toFixed(1)} MCF
                    </p>
                  </div>
                )}
              </div>

              {/* Status and Last Check */}
              <div className="flex items-center justify-between">
                {!isDraft && well.lastEntry ? (
                  <div className="flex items-center space-x-1.5">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className={`text-sm ${isWellOverdue ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                      {formatTimeAgo(well.lastEntry)}
                    </span>
                  </div>
                ) : isDraft ? (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Not yet created</span>
                ) : (
                  <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">Never checked</span>
                )}

                {!isDraft && well.status && (
                  <Badge 
                    className={
                      well.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                      well.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                    }
                    variant="secondary"
                  >
                    {well.status}
                  </Badge>
                )}
              </div>
            </div>
          </GradientCard>
        );
      })}
    </div>
  );
}